import { useId, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import type { PersonalSkillDraft } from '@hector/shared-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SkillInstructionsDialog } from './SkillInstructionsDialog';
import { normalizeSkillSlugInput } from './skillUtils';

export interface SkillFieldErrors {
  slug?: string;
  description?: string;
  content?: string;
}

interface SkillEditorFieldsProps {
  value: PersonalSkillDraft;
  onChange(value: PersonalSkillDraft): void;
  errors?: SkillFieldErrors;
  disabled?: boolean;
  commandDisabled?: boolean;
}

export function SkillEditorFields({
  value,
  onChange,
  errors = {},
  disabled = false,
  commandDisabled = false,
}: SkillEditorFieldsProps) {
  const id = useId();
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-slug`} className="text-xs font-medium text-grey-700">
          Nom de la compétence
        </Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-grey-400">/</span>
          <Input
            id={`${id}-slug`}
            value={value.slug}
            onChange={(event) => onChange({
              ...value,
              slug: normalizeSkillSlugInput(event.target.value),
            })}
            disabled={disabled || commandDisabled}
            className="bg-white pl-6 text-sm"
            placeholder="chronologie-juridique"
            aria-invalid={Boolean(errors.slug)}
          />
        </div>
        <p className="text-xs text-grey-400">Ce nom devient aussi la commande /nom.</p>
        {errors.slug ? <p className="text-xs text-red-600">{errors.slug}</p> : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-description`} className="text-xs font-medium text-grey-700">
          Description
        </Label>
        <textarea
          id={`${id}-description`}
          value={value.description}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
          disabled={disabled}
          rows={3}
          className="w-full resize-y rounded-lg border border-grey-200 bg-white px-3 py-2 text-sm text-grey-900 outline-none transition focus-visible:border-grey-500 focus-visible:ring-2 focus-visible:ring-grey-200"
          placeholder="Quand Hector doit-il utiliser cette méthode ?"
          aria-invalid={Boolean(errors.description)}
        />
        <p className="text-xs text-grey-400">
          La description aide Hector à reconnaître quand cette compétence est pertinente.
        </p>
        {errors.description ? <p className="text-xs text-red-600">{errors.description}</p> : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${id}-content`} className="text-xs font-medium text-grey-700">
          Instructions
        </Label>
        <div className="relative">
          <textarea
            id={`${id}-content`}
            value={value.content}
            onChange={(event) => onChange({ ...value, content: event.target.value })}
            disabled={disabled}
            rows={8}
            className="w-full resize-y rounded-lg border border-grey-200 bg-white px-3 py-3 pr-11 font-mono text-sm leading-relaxed text-grey-900 outline-none transition focus-visible:border-grey-500 focus-visible:ring-2 focus-visible:ring-grey-200"
            placeholder="# Méthode\n\nDécrivez les étapes à suivre…"
            aria-invalid={Boolean(errors.content)}
          />
          <button
            type="button"
            onClick={() => setInstructionsOpen(true)}
            disabled={disabled}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-grey-200 bg-white text-grey-500 shadow-sm transition hover:bg-grey-50 hover:text-grey-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grey-400 disabled:opacity-50"
            aria-label="Agrandir les instructions"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {errors.content ? <p className="text-xs text-red-600">{errors.content}</p> : null}
      </div>

      <SkillInstructionsDialog
        open={instructionsOpen}
        onOpenChange={setInstructionsOpen}
        value={value.content}
        onChange={(content) => onChange({ ...value, content })}
        disabled={disabled}
      />
    </div>
  );
}
